import { OrganizationFeatureFlag } from '@doss/common-all'
import { alpha } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { FiBarChart2, FiCode, FiFileText, FiHash } from 'react-icons/fi'
import { RxTable } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { useGetSimpleFormsV2Query } from '../../common/generated/apollo-operations'
import { useGlobalContext } from '../../common/hooks/useGlobalContext'
import { useTypedTranslation } from '../../common/hooks/useTypedTranslation'
import { TablePicker } from '../charts/TablePicker'
import { PivotTableIcon } from '../pivot-table/icons/PivotTableIcon'
import { SimpleTable } from '../tables/types'
import { Button } from '../ui/atoms/button/Button'
import { LoadingSpinner } from '../ui/atoms/loading-spinner/LoadingSpinner'
import { MenuItem } from '../ui/atoms/menu-item/MenuItem'
import { Menu } from '../ui/atoms/menu/Menu'

export enum BlockType {
  CHART = 'chart',
  METRIC = 'metric',
  TABLE = 'table',
  PIVOT_TABLE = 'pivot_table',
  FORM = 'form',
  EMBED = 'embed',
}

type CreateBlockButtonMenuProps = {
  buttonRef: HTMLElement | null
  open: boolean
  onSelect?: (block: { tableId: string; formId?: string; selectedBlockType: BlockType }) => void
  onClose?: () => void
}

export const CreateBlockButtonMenu = ({
  buttonRef,
  open,
  onSelect,
  onClose,
}: CreateBlockButtonMenuProps) => {
  const { classes } = useStyles()
  const [isOpen, setIsOpen] = useState(open)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerText, setPickerText] = useState('')
  const [selectedTable, setSelectedTable] = useState<SimpleTable | null>(null)
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null)
  const [selectedBlockType, setSelectedBlockType] = useState<BlockType>(BlockType.CHART)
  const { t } = useTypedTranslation()
  const { user, isInternal } = useGlobalContext()
  // Embed views are gated behind the org feature flag AND restricted to Doss-internal users.
  const embedEnabled =
    user?.organizationFeatureFlags[OrganizationFeatureFlag.EmbedViewType] === true &&
    isInternal === true

  const isFormBlock = selectedBlockType === BlockType.FORM

  // Only fetch the embeddable forms once the form picker is actually open, matching the lazy
  // behavior of mounting the picker on demand.
  const { data: formsData, loading: formsLoading } = useGetSimpleFormsV2Query({
    skip: !isFormBlock || !pickerOpen,
  })
  const formOptions = useMemo(
    () =>
      (formsData?.allFormsV2 || []).map((form) => ({
        value: form.id,
        label: form.name,
        startIcon: <FiFileText size={12} />,
      })),
    [formsData?.allFormsV2],
  )

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const blockMenuOptions: {
    blockType: BlockType
    label: string
    icon: React.ReactNode
    pickerText: string
  }[] = [
    {
      blockType: BlockType.CHART,
      label: t('charts.chart'),
      icon: <FiBarChart2 />,
      pickerText: t('workspace.select_data_source_for_new_chart'),
    },
    {
      blockType: BlockType.METRIC,
      label: t('charts.metric'),
      icon: <FiHash />,
      pickerText: t('workspace.select_data_source_for_new_metric'),
    },
    {
      blockType: BlockType.PIVOT_TABLE,
      label: t('charts.types.PIVOT_TABLE'),
      icon: <PivotTableIcon />,
      pickerText: t('workspace.select_data_source_for_new_pivot_table'),
    },
    {
      blockType: BlockType.TABLE,
      label: t('charts.table'),
      icon: <RxTable />,
      pickerText: t('workspace.select_table_to_add_to_dashboard'),
    },
    {
      blockType: BlockType.FORM,
      label: t('workspace.form'),
      icon: <FiFileText />,
      pickerText: t('workspace.select_form_to_add_to_dashboard'),
    },
  ]

  return (
    <>
      <Menu
        anchorEl={buttonRef}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={classes.menu}
        disableScrollLock
        onClick={() => setIsOpen(false)}
        onClose={() => {
          setIsOpen(false)
          onClose?.()
        }}
        open={isOpen}
        transformOrigin={{
          vertical: 8,
          horizontal: 'left',
        }}
      >
        {blockMenuOptions.map((option) => (
          <MenuItem
            key={option.blockType}
            disableRipple
            label={
              <>
                <span className="me-3 ms-1 size-4 text-[16px] text-gray-11">{option.icon}</span>
                {option.label}
              </>
            }
            onClick={() => {
              setPickerOpen(true)
              setPickerText(option.pickerText)
              setSelectedBlockType(option.blockType)
            }}
            showCheckmark={false}
            variant="default"
          />
        ))}
        {embedEnabled && (
          <MenuItem
            key={BlockType.EMBED}
            disableRipple
            label={
              <>
                <span className="me-3 ms-1 size-4 text-[16px] text-gray-11">
                  <FiCode />
                </span>
                {t('workspace.embed')}
              </>
            }
            onClick={() => {
              setIsOpen(false)
              onClose?.()
              onSelect?.({ tableId: '', selectedBlockType: BlockType.EMBED })
            }}
            showCheckmark={false}
            variant="default"
          />
        )}
      </Menu>
      <Menu
        anchorEl={buttonRef}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={classes.menu}
        disableScrollLock
        onClose={() => {
          setPickerOpen(false)
          onClose?.()
        }}
        open={pickerOpen}
        transformOrigin={{
          vertical: 8,
          horizontal: 'left',
        }}
      >
        <div className="flex flex-col gap-4 p-5">
          {isFormBlock ? (
            <div className="relative flex flex-col gap-0">
              <div className="flex flex-col gap-2 text-gray-9">
                <Dropdown
                  disabled={formsLoading}
                  label={pickerText}
                  onChange={(newValue) => setSelectedFormId(newValue || null)}
                  options={formOptions}
                  placeholder={pickerText}
                  startAdornment={
                    formsLoading ? <LoadingSpinner type="table-cell" /> : <FiFileText size={12} />
                  }
                  value={selectedFormId ?? undefined}
                />
              </div>
            </div>
          ) : (
            <TablePicker
              label={pickerText}
              onChange={setSelectedTable}
              placeholder={t('workspace.search_for_database')}
              requiresFullViewAccess={selectedBlockType !== BlockType.TABLE}
              selectedTable={selectedTable}
            />
          )}
          <div className="flex justify-end">
            <Button
              disabled={isFormBlock && !selectedFormId}
              onClick={() => {
                setPickerOpen(false)
                onClose?.()
                onSelect?.({
                  tableId: selectedTable?.id || '',
                  formId: selectedFormId || undefined,
                  selectedBlockType,
                })
              }}
              variant="primary"
            >
              {t('common.actions.select')}
            </Button>
          </div>
        </div>
      </Menu>
    </>
  )
}

const useStyles = tss.create(({ theme }) => {
  return {
    menu: {
      '& .MuiPaper-root': {
        borderRadius: 1,
        marginTop: theme.spacing(1),
        marginInlineStart: '-8px',
        minWidth: 180,
        color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
          'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
          padding: '0',
        },
        '& .MuiMenuItem-root': {
          paddingBlock: '12px',
          '&:active': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.selectedOpacity,
            ),
          },
        },
      },
    },
    menuItemIcon: {
      fontSize: '16px',
      color: theme.palette.text.secondary,
      marginInlineEnd: theme.spacing(1.5),
    },
  }
})
